import { Check, Circle } from 'lucide-react'
import { cn } from '../../../utils/cn'

interface AssessmentSection {
  id: string
  label: string
  isCompleted?: boolean
}

interface AssessmentSidebarProps {
  sections: AssessmentSection[]
  activeSection: string
  sectionsCompleted: number
  totalSections: number
  progressPercentage: number
  onSectionClick?: (sectionId: string) => void
  className?: string
}

export function AssessmentSidebar({
  sections,
  activeSection,
  sectionsCompleted,
  totalSections,
  progressPercentage,
  onSectionClick,
  className,
}: AssessmentSidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-screen w-[270px] flex-col border-r border-gray-200 bg-[#F7F7F7]',
        className
      )}
    >
      {/* Sections List */}
      <div className='flex-1 overflow-y-auto px-4 py-6'>
        <nav className='space-y-2'>
          {sections.map(section => {
            const isActive = activeSection === section.id
            const isCompleted = section.isCompleted

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick?.(section.id)}
                className={cn(
                  'relative flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-base font-medium transition-colors',
                  isActive && 'bg-white',
                  !isActive && 'hover:bg-gray-100'
                )}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className='absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[#F97316]' />
                )}

                <span
                  className={cn('text-gray-900', isActive && 'font-semibold')}
                >
                  {section.label}
                </span>

                {/* Status indicator */}
                {isCompleted ? (
                  <div className='flex h-5 w-5 items-center justify-center rounded-full bg-green-500'>
                    <Check className='h-3 w-3 text-white' strokeWidth={3} />
                  </div>
                ) : (
                  <Circle className='h-5 w-5 text-gray-300' strokeWidth={2} />
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Progress Footer */}
      <div className='border-t border-gray-200 px-6 py-6'>
        <div className='mb-3 rounded-full border border-gray-300 bg-white px-4 py-2 text-center text-sm text-gray-600'>
          {sectionsCompleted}/{totalSections} sections completed
        </div>
        <div className='flex items-center justify-between text-sm'>
          <span className='text-gray-600'>Progress Status</span>
          <span className='font-semibold text-[#F97316]'>
            {progressPercentage}%
          </span>
        </div>
      </div>
    </aside>
  )
}
